import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { EMPTY, Subject, combineLatest } from 'rxjs';
import { expand, filter, map, startWith, switchMap, take, takeUntil } from 'rxjs/operators';

import { noWhitespaceValidator } from '../../../../helpers/validators';
import { environment } from '../../../../../environments/environment';
import { ApiLavandierService } from '../../../../../lib/lib-ngx/web-services/api-lavandier.service';
import { ErrorMessage } from '../../../../../lib/lib-shared/ErrorMessage';

@Component({
  selector: 'lm-new-user',
  templateUrl: './new-user.component.html',
})
export class NewUserComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  form = this.fb.group({
    firstName: ['', [Validators.required, noWhitespaceValidator]],
    lastName: ['', [Validators.required, noWhitespaceValidator]],
    email: ['', [Validators.required, Validators.email]],
    admin: [true],
    internalAdmin: [false],
  });

  constructor(
    private fb: FormBuilder,
    private apiLavandierService: ApiLavandierService,
    private router: Router,
  ) { }

  ngOnInit() {
    this.updateEmailOnNameChange();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateEmailOnNameChange() {
    const firstNameControl = this.form.controls['firstName'];
    const lastNameControl = this.form.controls['lastName'];

    combineLatest([
      firstNameControl.valueChanges.pipe(startWith(firstNameControl.value)),
      lastNameControl.valueChanges.pipe(startWith(lastNameControl.value)),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([firstName, lastName]) => {
        const email = this.generateUniqueEmail(firstName, lastName);
        this.form.controls['email'].setValue(email, { emitEvent: false });
        this.form.updateValueAndValidity();
      });
  }

  onNewLavandier() {
    const { lastName, firstName, admin, internalAdmin } = this.form.value;
    this.apiLavandierService
      .getUserTypeList({})
      .pipe(
        map(userTypeList => userTypeList.find(userType => userType.default)),
        switchMap(userTypeDefault =>
          this.postUser(firstName, lastName, userTypeDefault.id, 0).pipe(
            expand(res => res.error === ErrorMessage.EMAIL_ALREADY_USED
              ? this.postUser(firstName, lastName, userTypeDefault.id, res._index + 1)
              : EMPTY,
            ),
            filter(res => !res.error),
            take(1),
          )
        ),
        switchMap(res => this.apiLavandierService.postLavandier({
          userId: res.id,
          admin: admin,
          internalAdmin: internalAdmin,
        })),
      )
      .subscribe(lavandier => this.router.navigate(['/lavandier/details', lavandier.id]));
  }

  private postUser(firstName: string, lastName: string, userTypeId: number, index: number) {
    const email = this.generateUniqueEmail(firstName, lastName, index);
    this.form.controls['email'].setValue(email, { emitEvent: false });

    if (this.form.controls['email'].invalid) {
      return EMPTY;
    }

    return this.apiLavandierService.postUser({
      userTypeId: userTypeId,
      dataConsent: false,
      multipleArticle: false,
      orderName: false,
      email: email,
      firstName: firstName,
      lastName: lastName,
      phoneNumber: '+33612345678'
    })
      .pipe(map(res => ({ ...res, _index: index })));
  }

  private generateUniqueEmail(firstName: string, lastName: string, i: number = 0): string {
    const initials = this.getFirstNameInitials(firstName);
    const cleanLast = this.sanitizeEmailLocalPart(lastName);

    if (!initials || !cleanLast) {
      return '';
    }

    const local =`${initials}.${cleanLast}`.toLowerCase();
    return `${local}${i > 0 ? `-${i}` : ''}@${environment.emailDomain}`;
  }

  private getFirstNameInitials(firstName: string): string {
    return (firstName || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .split(/[\s-]+/)
      .filter(Boolean)
      .map(part => part[0])
      .join('')
      .toLowerCase();
  }

  private sanitizeEmailLocalPart(str: string): string {
    return (str || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '-')
      .replace(/[^a-zA-Z0-9.-]/g, '');
  }

}
